import React from 'react';
import { motion } from 'framer-motion';
import { paragraphsOf, type WrapUpNext, type WrapUpNumber, type WrapUpQuote } from '../../utils/wrapUp';

/**
 * THE WRAP-UP — the studio's own post-show report, on the client's page.
 *
 * **THIS FILE DISPLAYS A DOCUMENT IT DOES NOT OWN.** Tom: *"The PDF wrap
 * report is ALL important. Stop writing it in your own words."* Every word
 * is Shilpa's, verbatim. The layout bends to the copy, never the reverse.
 *
 * NOTHING IS SET IN A NARROW LEFT COLUMN. Four rounds died here. Tom:
 * *"Some of the text is spread out, then back to just putting it down the
 * left side again."* Every block below occupies the full width by a
 * different means: figures and groups are CARDS, prose runs in TWO
 * COLUMNS, the closing sits on an INK BLOCK whose padding supplies the
 * white space, and every display line is measured in `ch` at a width that
 * actually reaches the right margin at its own size — a 21ch cap on 60px
 * type stops two thirds of the way across a 1180px page. If you add a
 * block here, give it a right edge.
 *
 * SECTION MARKERS ARE NUMBERED LABELS, NOT HEADINGS. A 36px "Where this
 * could go" directly above Shilpa's 48px "Moving forward, we want to take
 * ATM TV…" read as two headings quarrelling — Tom: *"image three is
 * fighting for what the heading is."* The marker is now small caps over a
 * full-width ink rule: unmistakably a divider, never a rival. The report's
 * own sentence carries the weight.
 */

const EASE = [0.22, 1, 0.36, 1] as const;

/** Spacing that opens a section. The rule belongs to SectionMark. */
export const SECTION_TOP = 'mt-24 scroll-mt-8';

/** A card. One surface for every discrete item on the page. */
export const CARD = 'flex h-full flex-col border border-rule bg-paperDeep/50 p-7 sm:p-8';

/**
 * Rise and fade, once, as a block comes into view. Deliberately small —
 * 18px and half a second. The page is a client's record of their project,
 * not a showreel, and anything larger reads as a website showing off.
 */
export function Reveal({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return <motion.div className={className} initial={{
    opacity: 0,
    y: 18
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.15
  }} transition={{
    duration: 0.5,
    ease: EASE,
    delay
  }} data-id="element-269" data-mp="1xpmztdj55izh">
      {children}
    </motion.div>;
}

/** The divider between parts of the report: 01 · YOUR FILMS, over ink. */
export function SectionMark({
  n,
  title
}: {
  n: string;
  title: string;
}) {
  return <div className="flex items-baseline gap-5 border-t border-ink pt-5" data-id="element-270" data-mp="1e2f9chcq4n51">
      <span className="label m-0 text-mute" data-id="element-271" data-mp="1j0h25tzyc0tp">{n}</span>
      <h2 className="label m-0 text-ink" data-id="element-272" data-mp="1j19xlytgn3wj">{title}</h2>
    </div>;
}

/**
 * One figure: the numeral, then the studio's own phrase.
 *
 * A CARD, NOT A BARE COLUMN. The phrase is a sentence — "24 high-level
 * studio interviews with industry leaders and decision-makers" — and a
 * sentence set loose on paper has no right edge. The card gives it one and
 * evens the heights of a row whose captions run to different lengths.
 *
 * A WIDE CARD TURNS ON ITS SIDE. A card spanning two or more columns holds
 * one numeral and eight words, which is mostly empty box standing up.
 * Laid out side by side the same card is full.
 *
 * Not every figure is a numeral: "Multiple soundbites for ATM's content
 * circulation" sets smaller so it holds the row without shouting.
 */
function Figure({
  value,
  label,
  wide = false
}: WrapUpNumber & {
  wide?: boolean;
}) {
  const numeral = /^[0-9]/.test(value);
  return <div className={`${CARD} ${wide ? 'sm:flex-row sm:items-baseline sm:gap-10' : ''}`} data-id="element-273" data-mp="1j24q20qq1y67">
      <p className={['m-0 font-display leading-none tracking-tight', numeral ? 'text-5xl sm:text-6xl' : 'text-3xl sm:text-4xl', wide ? 'sm:shrink-0' : ''].join(' ')} data-id="element-274" data-mp="1e4cy56q2uc20">
        {value}
      </p>
      <p className={`m-0 text-[0.95rem] leading-snug text-graphite ${wide ? 'mt-5 sm:mt-0' : 'mt-6'}`} data-id="element-275" data-mp="3hkzqum6ebdzt">
        {label}
      </p>
    </div>;
}
const SM_SPAN = ['', 'sm:col-span-1', 'sm:col-span-2', 'sm:col-span-3', 'sm:col-span-4'];

/**
 * A band of figures: four columns, always.
 *
 * THE LAST ROW IS ALWAYS FULL. Twelve figures used to leave the twelfth
 * alone in the first of four columns with a hole beside it — the same
 * stranding this page exists to stop, found by running twelve through the
 * scratch project rather than by reasoning about it. The leftovers now
 * widen to fill the row: one spans the lot, two take half each, three
 * leave the last one double. A widened card lays itself out sideways, so
 * it is a full card rather than a stretched one.
 *
 * **THE TRACK IS FIXED AT FOUR (16 Oct 2026).** It used to be the figure
 * count, capped at four — so a report whose figures split four-then-three
 * across the closing block drew a four-column grid and then a
 * three-column one, and the tile widths changed halfway down the page.
 * ATM's does exactly that. One grid for every band means the columns line
 * up through the whole report; the widening rule above still fills the
 * short row, so a band of three is 1 + 1 + 2 rather than three fat tiles
 * that agree with nothing above them.
 *
 * Phones keep the old behaviour — two columns, or one when there are only
 * a couple of figures and a 48px numeral would be squeezed — and an odd
 * last card spans both for the same reason.
 */
export function WrapUpFigures({
  figures
}: {
  figures: WrapUpNumber[];
}) {
  const count = figures.length;
  if (count === 0) return null;
  const track = 4;
  const cols = count <= 2 ? 'grid-cols-1 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-4';

  /* How many sit on the final, short row. */
  const rest = count % track;
  const spanOf = (index: number) => {
    if (rest === 0 || index < count - rest) return 1;
    if (rest === 1) return track;
    if (track - rest === 1) return index === count - 1 ? 2 : 1;
    return Math.floor(track / rest);
  };
  return <div className={`grid gap-5 ${cols}`} data-id="element-276" data-mp="1r66kt5lo6em">
      {figures.map((figure, index) => {
      const span = spanOf(index);
      const oddLastOnPhone = count >= 3 && count % 2 === 1 && index === count - 1;
      return <Reveal key={`${figure.value}-${index}`} delay={index * 0.06} className={`h-full ${oddLastOnPhone ? 'col-span-2' : ''} ${SM_SPAN[span]}`} data-id="element-277" data-mp="3o5zaydfmdsqs" data-mpi={index}>
            <Figure value={figure.value} label={figure.label} wide={span >= 2 || count <= 2} data-id="element-278" data-mp="kqaa5medh7yw" />
          </Reveal>;
    })}
    </div>;
}

/**
 * The line the report opens with. Deliberately NOT the `<h1>` — that is the
 * project's name, and a client arriving at a link needs to know which
 * project they are looking at before they are told how it went.
 */
export function WrapUpStatement({
  statement
}: {
  statement: string;
}) {
  if (!statement) return null;
  return <p className="m-0 max-w-[32ch] font-display text-[2.5rem] font-light leading-[1.05] tracking-tight sm:text-[3.75rem]" data-id="element-279" data-mp="2nyalrvg5r7pc">
      {statement}
    </p>;
}

/**
 * Prose from the report, in two columns.
 *
 * This is the whole trick. One column at a comfortable 66ch fills 560px and
 * strands the rest of the page; two of them fill it, and each paragraph
 * still reads at 66ch. Nothing is cut and nothing is stretched.
 *
 * An odd last paragraph runs the full width rather than sitting alone in
 * the left column — which is the very thing this component exists to stop,
 * and in this report that paragraph is the line introducing the cards
 * below it.
 */
export function WrapUpProse({
  text,
  className = ''
}: {
  text: string;
  className?: string;
}) {
  const paragraphs = paragraphsOf(text);
  if (paragraphs.length === 0) return null;
  const oddLast = paragraphs.length % 2 === 1 && paragraphs.length > 1;
  return <div className={`grid gap-x-12 gap-y-6 sm:grid-cols-2 ${className}`} data-id="element-280" data-mp="1hj9ysf941zuw">
      {paragraphs.map((paragraph, index) => <p key={index} className={['m-0 text-[1.05rem] leading-relaxed text-charcoal', oddLast && index === paragraphs.length - 1 ? 'sm:col-span-2' : ''].join(' ')} data-id="element-281" data-mp="w0gtak5v1ywi" data-mpi={index}>
          {paragraph}
        </p>)}
    </div>;
}

/**
 * Shilpa's closing judgement, on ink. The one dark block on a light page,
 * placed at its middle: the report's verdict, and the moment the scroll
 * changes temperature. Its padding is the white space, so the short
 * paragraph cannot strand itself on open paper.
 *
 * It is also what separates the two figure bands around it. Without a
 * closing paragraph the page must not simply drop it and let the bands
 * collide — see the band split in Overview.
 *
 * `aside` is the report photograph that sits beside the paragraph, when
 * there is one to spare. It arrives as a node rather than as a photo
 * object on purpose: this file must not import the photo components, or
 * WrapUp and WrapUpPhotos import each other and the cycle is a real
 * problem in a bundle. The page composes them instead.
 */
export function WrapUpClosing({
  text,
  aside
}: {
  text: string;
  aside?: React.ReactNode;
}) {
  const paragraphs = paragraphsOf(text);
  if (paragraphs.length === 0) return null;
  return <Reveal data-id="element-282" data-mp="396wzx79sns1q">
      <div className={['bg-ink px-8 py-16 text-paper sm:px-14 sm:py-20', aside ? 'sm:flex sm:items-center sm:gap-14' : ''].join(' ')} data-id="element-283" data-mp="397g7pv5qd021">
        <div className={aside ? 'sm:flex-1' : ''} data-id="element-284" data-mp="5q7tdla7oic2">
          {paragraphs.map((paragraph, index) => <p key={index} className="mt-8 m-0 max-w-[48ch] font-display text-2xl font-light leading-snug first:mt-0 sm:text-[2rem]" data-id="element-285" data-mp="xept0qhbg1k" data-mpi={index}>
              {paragraph}
            </p>)}
        </div>
        {aside}
      </div>
    </Reveal>;
}
function Quote({
  quote
}: {
  quote: WrapUpQuote;
}) {
  return <figure className={`m-0 ${CARD}`} data-id="element-286" data-mp="ajf9qk4xzwo8">
      <blockquote className="m-0 flex-1" data-id="element-287" data-mp="akgmqu5iqt8l">
        <p className="m-0 text-[1.05rem] leading-relaxed text-charcoal" data-id="element-288" data-mp="ali055a0brth">{quote.body}</p>
      </blockquote>
      {(quote.name || quote.role) && <figcaption className="mt-7 border-t border-rule pt-5" data-id="element-289" data-mp="khv0un3yz5xq">
          {quote.name && <span className="block text-[1rem] text-ink" data-id="element-290" data-mp="firtux4fipn4">{quote.name}</span>}
          {quote.role && <span className="mt-1.5 block text-[0.9rem] leading-snug text-mute" data-id="element-291" data-mp="thrkgabi5elx">{quote.role}</span>}
        </figcaption>}
    </figure>;
}

/**
 * What people said. Printed exactly as given and never trimmed to a
 * pull-quote — shortening someone's endorsement so it sits better in a
 * column is the kind of thing that ends a relationship with a client's
 * communications team.
 *
 * `before` is the row of photographs the page places above this section,
 * passed in for the same no-cycle reason as `aside` above.
 */
export function WrapUpVoices({
  quotes,
  n,
  before
}: {
  quotes: WrapUpQuote[];
  n: string;
  before?: React.ReactNode;
}) {
  if (quotes.length === 0) return null;
  return <section id="said" className={SECTION_TOP} data-id="element-292" data-mp="1vusw5z818aop">
      {before && <div className="mb-16" data-id="element-293" data-mp="1huryesn2djdg">{before}</div>}
      <SectionMark n={n} title="What they said" data-id="element-294" data-mp="1htqc1s0llagf" />
      <div className="mt-10 grid gap-5 sm:grid-cols-2" data-id="element-295" data-mp="1hx4bardyt3jn">
        {quotes.map((quote, index) => <Reveal key={`${quote.name}-${index}`} delay={index * 0.06} className="h-full" data-id="element-296" data-mp="1mvpft30929dw" data-mpi={index}>
            <Quote quote={quote} data-id="element-297" data-mp="182vq6or9kcvi" />
          </Reveal>)}
      </div>
    </section>;
}

/**
 * WHERE THIS COULD GO — the report's own "What's next?", last on the page.
 * After the files, so a client who came for their downloads never scrolls
 * past Portal selling to reach them.
 *
 * THE GROUPS ARE THREE CARDS ACROSS. Tom: *"these three items need to be
 * shown better - not a list down the left hand side."* They hold 3, 2 and 5
 * points; the cards stretch to the tallest rather than the points being
 * evened out by deleting any.
 */
export function WrapUpNextSection({
  next,
  n,
  before
}: {
  next: WrapUpNext | null;
  n: string;
  before?: React.ReactNode;
}) {
  if (!next) return null;
  return <section id="next" className={SECTION_TOP} data-id="element-298" data-mp="2n22oqlp2xu7c">
      {before && <div className="mb-16" data-id="element-299" data-mp="2n2azo81n6pfw">{before}</div>}
      <SectionMark n={n} title="Where this could go" data-id="element-300" data-mp="2i386plyxs9oa" />

      {next.heading && <Reveal data-id="element-301" data-mp="2i1584djpx87y">
          <p className="mt-10 m-0 max-w-[38ch] font-display text-[2rem] font-light leading-[1.1] tracking-tight sm:text-[3rem]" data-id="element-302" data-mp="3esz2doiys2vo">
            {next.heading}
          </p>
        </Reveal>}

      {next.body && <Reveal data-id="element-303" data-mp="35rrdfx2c8ee9">
          <WrapUpProse text={next.body} className="mt-12" data-id="element-304" data-mp="35r96a5e50frk" />
        </Reveal>}

      {next.groups.length > 0 && <div className="mt-14 grid gap-5 md:grid-cols-3" data-id="element-305" data-mp="30t7n7kgpogg3">
          {next.groups.map((group, index) => <Reveal key={`${group.title}-${index}`} delay={index * 0.08} className="h-full" data-id="element-306" data-mp="1ubau3snnxdm4" data-mpi={index}>
              <div className={CARD} data-id="element-307" data-mp="1pgdmmq617cbc">
                {group.title && <h3 className="m-0 font-display text-xl leading-none tracking-tight sm:text-2xl" data-id="element-308" data-mp="1fbosc0gs6hu8">
                    {group.title}
                  </h3>}
                {group.points.length > 0 && <ul className="m-0 mt-7 list-none p-0" data-id="element-309" data-mp="1p6gs9axu5gs6">
                    {group.points.map((point, at) => <li key={at} className="mt-5 border-t border-rule pt-5 text-[1rem] leading-relaxed text-charcoal first:mt-0 first:border-0 first:pt-0" data-id="element-310" data-mp="1u138brev3xil" data-mpi={at}>
                        {point}
                      </li>)}
                  </ul>}
              </div>
            </Reveal>)}
        </div>}
    </section>;
}